import { useUserGroupsList, useUsers } from '@src/hooks';
import type { UserGroup } from '@src/types';
import {
  Box,
  ButtonVariants,
  Case,
  FormGroup,
  HStack,
  Icon,
  IconName,
  IndicatorBadge,
  ROLE,
  Stack,
  Switch,
  ToggleGroup,
  VStack,
  type ColumnDef,
  type OtherUser,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { useCallback, useMemo } from 'react';
import { ALL_ROLE_ITEMS, prettifyRole, type RoleItem } from '../../../../tokens/roles';
import type { LayoutSpecShareTarget } from '../../../AppLayout/LayoutSpecs/LayoutSpecSlice';
import { SelectionBlotter } from './SelectionBlotter';

export type ShareType = 'roles' | 'userGroups' | 'users' | 'public' | 'private';

export function ShareSelector({
  shareType,
  onShareTypeChange,
  shares,
  onSharesChange,
}: {
  shareType: ShareType;
  onShareTypeChange: (shareType: ShareType) => void;
  shares: LayoutSpecShareTarget[];
  onSharesChange: (shares: LayoutSpecShareTarget[]) => void;
}) {
  const userGroups = useUserGroupsList();
  const users = useUsers();
  const roles = useAvailableRoles();

  const handleUsersSelectionChanged = useCallback(
    (selectedUsers: OtherUser[]) => {
      onSharesChange(selectedUsers.map(user => ({ User: user.Name })));
    },
    [onSharesChange]
  );

  const handleUserGroupsSelectionChanged = useCallback(
    (selectedUserGroups: UserGroup[]) => {
      onSharesChange(selectedUserGroups.map(group => ({ GroupID: group.GroupID })));
    },
    [onSharesChange]
  );

  const handleRolesSelectionChanged = useCallback(
    (selectedRoles: RoleItem[]) => {
      onSharesChange(selectedRoles.map(role => ({ Role: role.value })));
    },
    [onSharesChange]
  );

  const selectedUsers = useMemo(() => {
    const userNames = new Set(
      shares
        .filter((share): share is { User: string } => 'User' in share && share.User != null)
        .map(share => share.User)
    );
    return users.filter(user => userNames.has(user.Name));
  }, [shares, users]);

  const selectedRoles = useMemo(() => {
    const roleValues = new Set(
      shares
        .filter((share): share is { Role: string } => 'Role' in share && share.Role != null)
        .map(share => share.Role)
    );
    return roles.filter(role => roleValues.has(role.value));
  }, [shares, roles]);

  const selectedUserGroups = useMemo(() => {
    const groupIDs = new Set(
      shares
        .filter((share): share is { GroupID: string } => 'GroupID' in share && share.GroupID != null)
        .map(share => share.GroupID)
    );
    return (userGroups ?? []).filter(group => groupIDs.has(group.GroupID));
  }, [shares, userGroups]);

  const usersColumns: ColumnDef<OtherUser>[] = useMemo(
    () => [
      { type: 'text', field: 'Name', title: 'Name', flex: 1 },
      {
        id: 'roles',
        type: 'custom',
        field: 'Roles',
        title: 'Roles',
        params: {
          cellRenderer: ({ value }: ICellRendererParams<ROLE[]>) => (
            <HStack gap="spacing4">
              {value?.map((role: ROLE) => (
                <IndicatorBadge key={role}>{prettifyRole(role)}</IndicatorBadge>
              ))}
            </HStack>
          ),
        } satisfies ColDef,
        sortable: true,
      },
    ],
    []
  );

  const userGroupsColumns: ColumnDef<UserGroup>[] = useMemo(
    () => [{ type: 'text', field: 'DisplayName', title: 'Group', flex: 1 }],
    []
  );

  const rolesColumns: ColumnDef<RoleItem>[] = useMemo(
    () => [{ type: 'text', field: 'label', title: 'Role', flex: 1 }],
    []
  );

  return (
    <>
      <FormGroup label="Access">
        <ToggleGroup
          options={[
            { value: 'public', displayName: 'Public' },
            { value: 'private', displayName: 'Private' },
            { value: 'roles', displayName: 'User Roles' },
            { value: 'userGroups', displayName: 'User groups' },
            { value: 'users', displayName: 'Users' },
          ]}
          defaultSelectedVariant={ButtonVariants.Priority}
          value={shareType}
          onChange={onShareTypeChange}
          data-testid="toggle-share-type"
        />
      </FormGroup>
      <Stack flex="1 1 auto" borderColor="backgroundDivider" border="solid 1px" borderRadius="borderRadiusDefault">
        <Switch value={shareType}>
          <Case value="public">
            <VStack p="spacing32" flex="1 1 auto">
              <Icon icon={IconName.Users} size={32} />
              <Box fontSize="fontSizeTitleH4" color="colorTextImportant" my="spacing12">
                Public
              </Box>
              <Box fontSize="fontSizeDefault" color="colorTextSubtle" textAlign="center" textWrap="pretty">
                This template will be visible to all users in the organization.
              </Box>
            </VStack>
          </Case>
          <Case value="private">
            <VStack p="spacing32" flex="1 1 auto">
              <Icon icon={IconName.LockClosed} size={32} />
              <Box fontSize="fontSizeTitleH4" color="colorTextImportant" my="spacing12">
                Private
              </Box>
              <Box fontSize="fontSizeDefault" color="colorTextSubtle" textAlign="center">
                This template will only be visible to you.
              </Box>
            </VStack>
          </Case>
          <Case value="users">
            <SelectionBlotter
              key="users"
              availableItems={users}
              selectedItems={selectedUsers}
              onSelectionChanged={handleUsersSelectionChanged}
              columns={usersColumns}
              rowID="ID"
              searchPlaceholder="Search users..."
            />
          </Case>
          <Case value="userGroups">
            <SelectionBlotter
              key="userGroups"
              availableItems={userGroups ?? []}
              selectedItems={selectedUserGroups}
              onSelectionChanged={handleUserGroupsSelectionChanged}
              columns={userGroupsColumns}
              rowID="GroupID"
              searchPlaceholder="Search user groups..."
            />
          </Case>
          <Case value="roles">
            <SelectionBlotter
              key="roles"
              availableItems={roles}
              selectedItems={selectedRoles}
              onSelectionChanged={handleRolesSelectionChanged}
              columns={rolesColumns}
              rowID="value"
              searchPlaceholder="Search roles..."
            />
          </Case>
        </Switch>
      </Stack>
    </>
  );
}

const BLACKLISTED_ROLES: ROLE[] = [
  ROLE.NONE,
  ROLE.MARKETPLACE_ADMIN,
  ROLE.MARKETPLACE_OPS,
  ROLE.MARKETPLACE_TRADER,
  ROLE.MARKETPLACE_VIEWER,
  ROLE.PORTFOLIO_BETA,
  ROLE.TALOS_ADMIN,
  ROLE.TALOS_ONBOARDING_ADMIN,
  ROLE.TALOS_SUPPORT,
  ROLE.TALOS_TEST_RUNNER,
  ROLE.TALOS_VIEWER,
  ROLE.TALOS_CANCEL_ORDERS,
  ROLE.TRANSFERS_HIDDEN,
];
function useAvailableRoles() {
  // TODO Filter roles based on enabled features
  return useMemo(() => ALL_ROLE_ITEMS.filter(role => !BLACKLISTED_ROLES.includes(role.value)), []);
}
